import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useBooking } from '../contexts/BookingContext';
import { useTimer } from '../contexts/TimerContext';
import { useSettings } from '../contexts/SettingsContext';
import SeatSelection from '../components/SeatSelection';
import ProgressStepper from '../components/ProgressStepper';
import CancelTransactionPopup from '../components/CancelTransactionPopup';
import { api } from '../config/api';
import './SeatSelectionPage.css';

const SeatSelectionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    movie: bookingMovie,
    showtime: bookingShowtime,
    theater: bookingTheater,
    selectedSeats,
    setSelectedSeats,
    setMovie,
    setShowtime,
    setTheater,
    vistaTransactionId: bookingTransactionId,
    setVistaTransactionId,
    quantity,
    setQuantity,
  } = useBooking();
  const { isExpired, resetTimer, startTimer } = useTimer();
  const { getBookingSettings } = useSettings();
  const [bookingSettings, setBookingSettings] = useState(null);

  // Initialize localSelectedSeats from location state or global state
  const initialSelectedSeats = location.state?.selectedSeats || selectedSeats || [];
  const [localSelectedSeats, setLocalSelectedSeats] = useState(initialSelectedSeats);
  const [isConfirmingSeats, setIsConfirmingSeats] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
  // Store the latest transaction ID locally to ensure it's available immediately after quantity change
  const [latestTransactionId, setLatestTransactionId] = useState(null);
  // Use a ref to store the latest transaction ID for immediate synchronous access
  const latestTransactionIdRef = useRef(null);
  // Back navigation confirmation
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancellingTransaction, setIsCancellingTransaction] = useState(false);
  const [availableShowtimes, setAvailableShowtimes] = useState([]);
  const [isSwitchingShowtime, setIsSwitchingShowtime] = useState(false);
  const [pendingSwitchShowtime, setPendingSwitchShowtime] = useState(null);
  const [showtimeSwitcherOpen, setShowtimeSwitcherOpen] = useState(false);
  const [overrideShowtime, setOverrideShowtime] = useState(null)
  const [isQuantityModalAfterCancel, setIsQuantityModalAfterCancel] = useState(false);
  const [hasProceeded, setHasProceeded] = useState(false);
  const [restoreChecked, setRestoreChecked] = useState(false);
  const isNavigatingBackRef = useRef(false);
  const [showSeatWarning, setShowSeatWarning] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  useEffect(() => {
    if (!location.state) {
      const saved = sessionStorage.getItem('seat-booking');
      if (saved) {
        const parsed = JSON.parse(saved);

        setMovie(parsed.movie);
        setShowtime(parsed.showtime);
        setTheater(parsed.theater);
      }
    }
  }, []);



  // Get movie and showtime data from navigation state or use defaults
  const movie = useMemo(() => {
    if (location.state?.movie) return location.state.movie;
    if (bookingMovie) return bookingMovie;
    return null;
  }, [location.state?.movie, bookingMovie]);

  const showtime = useMemo(() => {
    if (overrideShowtime) return overrideShowtime;  // ← switcher always wins showtimechange
    if (location.state?.showtime) return location.state.showtime;
    if (bookingShowtime) return bookingShowtime;
    return null;
  }, [overrideShowtime, location.state?.showtime, bookingShowtime]); //showtimechange

  const theater = useMemo(() => {
    if (location.state?.theater) return location.state.theater;
    if (bookingTheater) return bookingTheater;
    return null;
  }, [location.state?.theater, bookingTheater]);




  const movieId = movie?.id || movie?.vistaMovieId || null;


  const selectedDate = location.state?.selectedDate;


   // Get transaction ID and session ID from location state or booking context

    const locationTransactionId = location.state?.vistaTransactionId;
  const locationSessionId = location.state?.vistaSessionId;
  const fromQuickBooking = location.state?.fromQuickBooking || false;
  const currentSessionId = showtime?.id || showtime?.sessionId || showtime?.vistaShowId;

  const currentShowtimeAvailableSeats = useMemo(() => {
    const match = availableShowtimes.find(
      st => (st.id || st.sessionId || st.vistaShowId) === currentSessionId
    );
    if (match?.availableSeats != null) return match.availableSeats;
    if (showtime?.availableSeats != null) return showtime.availableSeats;
    return null;
  }, [availableShowtimes, currentSessionId, showtime?.availableSeats]);
  
  const maxSelectableQuantity = useMemo(() => {
    const settingsMax = bookingSettings?.maxSeatsPerBooking || 8;
    if (currentShowtimeAvailableSeats === null) return settingsMax;
    return Math.min(settingsMax, currentShowtimeAvailableSeats);
  }, [bookingSettings?.maxSeatsPerBooking, currentShowtimeAvailableSeats]);

   // Get transaction ID from context (it's stored as object or string)
  // Note: typeof null === 'object' in JavaScript, so we need to check for null explicitly
  const contextTransactionId = bookingTransactionId && typeof bookingTransactionId === 'object' 
    ? bookingTransactionId.transactionId 
    : (bookingTransactionId || null);
  const contextSessionId = bookingTransactionId && typeof bookingTransactionId === 'object' 
    ? bookingTransactionId.sessionId 
    : null;


  // If coming from quick booking, clear any existing transaction to start fresh
  // Use transaction ID from location state first, then context
    // But validate it matches the current session ID to prevent inconsistency
      // let vistaTransactionId = fromQuickBooking ? null : (locationTransactionId || contextTransactionId); showtime change
      const vistaTransactionId =
  latestTransactionIdRef.current ||
  latestTransactionId ||
  contextTransactionId ||
  locationTransactionId ||
  null;
      // Validate transaction ID matches current session
  // if (vistaTransactionId && currentSessionId) {    //comment out for showtimechange
  //   const transactionSessionId = locationSessionId || contextSessionId;
  //   if (transactionSessionId && transactionSessionId !== currentSessionId) {
  //      console.warn('Transaction ID does not match current session ID, clearing it', {
  //       transactionSessionId,
  //        currentSessionId,
  //       transactionId: vistaTransactionId,
  //     });
  //     vistaTransactionId = null; // Clear if it doesn't match
  //   }
  // }

  useEffect(() => {
    if (!bookingTransactionId || !showtime) return;
  
    if (isSwitchingShowtime) return; // ✅ ADD THIS
  
    const transactionSessionId =
      typeof bookingTransactionId === 'object'
        ? bookingTransactionId.sessionId
        : null;
  
    const currentSessionId =
      showtime?.id || showtime?.sessionId || showtime?.vistaShowId;
  
    if (
      transactionSessionId &&
      currentSessionId &&
      transactionSessionId !== currentSessionId
    ) {
      console.warn("🧹 Clearing stale transaction (safe)");
  
      setVistaTransactionId(null);
      setSelectedSeats([]);
    }
  }, [showtime?.id, isSwitchingShowtime]);

   useEffect(() => {
    if (movie && showtime && theater) {
      sessionStorage.setItem(
        'seat-booking',
        JSON.stringify({
          movie,
          showtime,
          theater,
          selectedDate,
        })
      );
    }
  }, [movie, showtime, theater, selectedDate]);

  useEffect(() => {
    setRestoreChecked(true);
  }, []);

  useEffect(() => {
    if (!quantity || currentShowtimeAvailableSeats === null) return;
    if (quantity > currentShowtimeAvailableSeats) {
      setShowSeatWarning(true);
    }
  }, [currentShowtimeAvailableSeats]); 

  // Clear transaction ID in context if coming from quick booking
  useEffect(() => {
    if (fromQuickBooking && bookingTransactionId) {
      // console.log('Clearing transaction ID for new quick booking');
      setVistaTransactionId(null);
    }
  }, [fromQuickBooking, bookingTransactionId, setVistaTransactionId]);

  // Update booking context with transaction ID from location state if available and valid
 useEffect(() => {
  if (!locationTransactionId || !locationSessionId) return;

  const currentSessionId =
    showtime?.id || showtime?.sessionId || showtime?.vistaShowId;

  if (locationSessionId !== currentSessionId) return; // ✅ ADD THIS

  if (!bookingTransactionId) {
    setVistaTransactionId({
      transactionId: locationTransactionId,
      sessionId: locationSessionId,
    });
  }
}, [locationTransactionId, locationSessionId, bookingTransactionId, showtime?.id]);    

  // Show quantity modal if flag is set (when navigating from snacks/payment after cancel)
  // Use a ref to track if we've already shown the modal to prevent reopening
  const hasShownQuantityModalRef = useRef(false);
  const quantityModalShownRef = useRef(false);

  // Reset quantity modal ref when coming from quick booking or when showtime/movie changes (new booking)
  useEffect(() => {
    if (fromQuickBooking || (showtime && theater && !quantity)) {
      // Reset the ref to allow showing modal again for new booking
      // console.log('Resetting quantity modal ref for new booking', { fromQuickBooking, showtime: showtime?.id, theater: theater?.id });
      quantityModalShownRef.current = false;
    }
  }, [fromQuickBooking, showtime?.id, theater?.id, quantity]);
   useEffect(() => {
    // Show quantity modal if:
    // 1. Flag is set in location state (from quick booking or after cancel), OR
    // 2. We have showtime/theater but no quantity set (first time navigation)
    // 3. Modal is not already showing
    // 4. We haven't already shown it for this navigation
    // 5. Quantity is not already set (if quantity is set, don't show modal)
    const shouldShowModal = (
      (location.state?.showQuantityModal || (!quantity && showtime && theater)) &&
       !showQuantityModal && 
      !quantityModalShownRef.current &&
      !quantity

       );

    if (shouldShowModal) {
      // console.log('Showing quantity modal', {
      //   showQuantityModal: location.state?.showQuantityModal,
      //   hasShowtime: !!showtime,
      //   hasTheater: !!theater,
      //   quantity,

      //    fromQuickBooking,
      // });

      // Only set isQuantityModalAfterCancel if explicitly coming from cancel flow
      // For quick booking, it's a fresh booking, not after cancel
      if (location.state?.showQuantityModal && isQuantityModalAfterCancel) {
        // Already set, keep it
      } else if (location.state?.showQuantityModal || fromQuickBooking) {
        // Coming from quick booking - don't set isQuantityModalAfterCancel
        setIsQuantityModalAfterCancel(false);
      }

      setShowQuantityModal(true);
      quantityModalShownRef.current = true;

      // Clear the flag from location state immediately to prevent reopening
      if (window.history && window.history.replaceState) {
        const currentState = window.history.state || {};
        const newState = {
          ...currentState,
          ...location.state,
          showQuantityModal: false, // Clear the flag
          fromQuickBooking: false, // Clear the quick booking flag too
        };
        window.history.replaceState(newState, '');
      }
    }
  }, [location.state?.showQuantityModal, showQuantityModal, isQuantityModalAfterCancel, quantity, showtime, theater, fromQuickBooking]);

  // Reset the ref when component unmounts or when we explicitly want to allow showing again
  useEffect(() => {
    return () => {
      // Reset on unmount
      quantityModalShownRef.current = false;
    };
  }, []);

  // Fetch all showtimes for same movie/date/theater (for showtime switcher)
useEffect(() => {
  // if (!movie || !theater || !selectedDate) return;
  if (!movie || !theater) return;

  const filmCode = movie.vistaMovieId || movie.id;
  const cinemaId = theater.vistaCinemaId || theater.vistaCinemaID || theater.id;
  const effectiveDate =
    selectedDate ||
    showtime?.businessDate ||
    showtime?.showDate ||
    new Date().toLocaleDateString("en-CA");

  // if (!filmCode || !cinemaId) return;
  if (!filmCode || !cinemaId || !effectiveDate) return;

  // api.getVistaShowtimesForDate(filmCode, cinemaId, selectedDate)
  api.getVistaShowtimesForDate(filmCode, cinemaId, effectiveDate)
    .then((data) => {
      // data is an array of cinemas each with showtimes; find our theater's showtimes
      const cinemaData = data.find(
        (c) => c.vistaCinemaId === cinemaId || c.id === cinemaId
      );
      const showtimes = cinemaData?.showtimes || [];
      setAvailableShowtimes(showtimes);
    })
    .catch(() => setAvailableShowtimes([]));
  }, [movie, theater, selectedDate, showtime?.businessDate, showtime?.showDate,]);
// }, [movie?.id, movie?.vistaMovieId, theater?.id, theater?.vistaCinemaId, selectedDate]);

  // Load booking settings from admin API
  const loadBookingSettings = useCallback(async () => {
    try {
      const settings = await getBookingSettings();
      setBookingSettings(settings);
      // console.log('SeatSelectionPage - booking settings loaded:', settings);
    } catch (error) {
      console.error('Error loading booking settings:', error);
    }
  }, [getBookingSettings]);

  useEffect(() => {
    loadBookingSettings();
  }, [loadBookingSettings]);

  // Listen for admin settings updates
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'admin_settings_updated') {
        // console.log('Admin settings updated, refreshing seat selection booking settings...');
        loadBookingSettings();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Update global booking state when component mounts or location state changes
  useEffect(() => {
    // Only update movie if it's different
    if (location.state?.movie) {
      const currentMovieId = bookingMovie?.id || bookingMovie?.vistaMovieId;
      const newMovieId = location.state.movie?.id || location.state.movie?.vistaMovieId;
      if (currentMovieId !== newMovieId) {
        setMovie(location.state.movie);
      }
    } else if (bookingMovie && !location.state?.movie) {
      // Only set if not already set
      const currentMovieId = bookingMovie?.id || bookingMovie?.vistaMovieId;
      if (!currentMovieId) {
        setMovie(bookingMovie);
      }
    }

    // Only update showtime if it's different
    if (location.state?.showtime) {
      const currentShowtimeId = bookingShowtime?.id || bookingShowtime?.sessionId || bookingShowtime?.vistaShowId;
      const newShowtimeId = location.state.showtime?.id || location.state.showtime?.sessionId || location.state.showtime?.vistaShowId;
      if (currentShowtimeId !== newShowtimeId) {
        setShowtime(location.state.showtime);
      }
    } else if (bookingShowtime && !location.state?.showtime) {
      // Only set if not already set
      const currentShowtimeId = bookingShowtime?.id || bookingShowtime?.sessionId || bookingShowtime?.vistaShowId;
      if (!currentShowtimeId) {
        setShowtime(bookingShowtime);
      }
    }

    // Only update theater if it's different
    if (location.state?.theater) {
      const currentTheaterId = bookingTheater?.id || bookingTheater?.vistaCinemaId;
      const newTheaterId = location.state.theater?.id || location.state.theater?.vistaCinemaId;
      if (currentTheaterId !== newTheaterId) {
        setTheater(location.state.theater);
      }
    } else if (bookingTheater && !location.state?.theater) {
      // Only set if not already set
      const currentTheaterId = bookingTheater?.id || bookingTheater?.vistaCinemaId;
      if (!currentTheaterId) {
        setTheater(bookingTheater);
      }
    }
  }, [
    location.state?.movie,
    location.state?.showtime,
    location.state?.theater,
    bookingMovie,
    bookingShowtime,
    bookingTheater,
    setMovie,
    setShowtime,
    setTheater,
  ]);

  // Sync localSelectedSeats when location state or global selectedSeats change
  useEffect(() => {
    const seatsFromState = location.state?.selectedSeats || selectedSeats || [];
    setLocalSelectedSeats(prevSeats => {
      // Only update if the seats are actually different
      const prevSeatsStr = JSON.stringify(prevSeats);
      const newSeatsStr = JSON.stringify(seatsFromState);
      if (prevSeatsStr !== newSeatsStr) {
        // Also update global state if it's different and came from location state
        if (location.state?.selectedSeats && JSON.stringify(seatsFromState) !== JSON.stringify(selectedSeats)) {
          setSelectedSeats(seatsFromState);
        }
        return seatsFromState;
      }
      return prevSeats;
    });
  }, [location.state?.selectedSeats, selectedSeats, setSelectedSeats]);


  // Handle timer expiration
  useEffect(() => {
    if (!isExpired || !movieId) return;

    setSelectedSeats([]);
    showError('Session expired! Your seat selection has been cleared.');
    resetTimer();

    navigate(`/booking/${movieId}${selectedDate ? `?date=${selectedDate}` : ''}`);
  }, [isExpired, movieId, selectedDate, navigate, resetTimer, setSelectedSeats]);


  useEffect(() => {
    if (!hasProceeded) return; // don't intercept if user hasn't proceeded
  
    window.history.pushState(null, '', window.location.href);
    
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setShowCancelConfirm(true);
    };
  
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasProceeded]);


  // Handle cancel transaction confirmation
  const handleCancelTransaction = async () => {
    setIsCancellingTransaction(true);

    try {
      // Clear selected seats and quantity
      setSelectedSeats([]);
      setLocalSelectedSeats([]);
      setQuantity(null);


       // Close confirmation popup
      setShowCancelConfirm(false);

      // Show quantity selection modal (mark it as after cancel)
      // The reinitialization will happen when user selects quantity in handleQuantitySelectAfterCancel
      setIsQuantityModalAfterCancel(true);
      setShowQuantityModal(true);
    } catch (error) {
      console.error('Error cancelling transaction:', error);
      showError(`Failed to cancel transaction: ${error.message || 'Please try again.'}`);
    } finally {
      setIsCancellingTransaction(false);
    }
  };

  // Handle quantity selection after cancel
  const handleQuantitySelectAfterCancel = async (newQuantity) => {
    const maxSeats = bookingSettings?.maxSeatsPerBooking || 8;
    if (!newQuantity || newQuantity < 1 || newQuantity > maxSeats) {
      showError(`Please select a valid quantity (1-${maxSeats})`);
      return;
    }

    setIsUpdatingQuantity(true);

    try {
      const vistaCinemaId = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.id;
      const vistaSessionId = showtime?.id || showtime?.sessionId || showtime?.vistaShowId;
      // Use ref first for immediate access, then state, then context
      const currentTransId = latestTransactionIdRef.current || latestTransactionId || (bookingTransactionId && typeof bookingTransactionId === 'object' 
        ? bookingTransactionId.transactionId 
        : (bookingTransactionId || null));

      if (!vistaCinemaId || !vistaSessionId) {
        throw new Error('Missing cinema or session information');
      }

      // Cancel existing transaction and create new one
      const bookingData = await api.reinitVistaBooking(
        vistaCinemaId,
        vistaSessionId,
        currentTransId || null
      );

      if (bookingData && bookingData.transactionId) {
        const newTransactionId = String(bookingData.transactionId).trim();

        // Update transaction ID in ref FIRST for immediate synchronous access
        latestTransactionIdRef.current = newTransactionId;

        // Update transaction ID in local state
        setLatestTransactionId(newTransactionId);

        // Update transaction ID in context
        setVistaTransactionId({
          transactionId: newTransactionId,
          sessionId: vistaSessionId,
        });

        // Also update location state transaction ID to ensure it's available immediately
        // This ensures the transaction ID is available even before context updates propagate
        if (window.history && window.history.replaceState) {
          const currentState = window.history.state || {};
          window.history.replaceState({
            ...currentState,
            ...location.state,
            vistaTransactionId: newTransactionId,
            vistaSessionId: vistaSessionId,
          }, '');
        }

        // Set quantity
        setQuantity(newQuantity);

        // Clear selected seats so user can select from any group
        setSelectedSeats([]);
        setLocalSelectedSeats([]);

        // Close modal
        setShowQuantityModal(false);
        setIsQuantityModalAfterCancel(false);
        quantityModalShownRef.current = true;
      } else {
        throw new Error('Failed to initialize new transaction');
      }
    } catch (error) {
      console.error('Error reinitializing transaction after quantity change:', error);
      showError(`Failed to update quantity: ${error.message || 'Please try again.'}`);
      // Reopen modal on error so user can try again
      setShowQuantityModal(true);
      setIsQuantityModalAfterCancel(true);
    } finally {
      setIsUpdatingQuantity(false);
    }
  };

  const handleBackClick = () => {
    if (hasProceeded) {
      setShowCancelConfirm(true);
    } else {
      sessionStorage.removeItem('seat-booking');
      navigate(-1); 
    }
  };


  const handleSeatSelect = (seats) => {
    setSelectedSeats(seats);
    setLocalSelectedSeats(seats);
  };

  const handleProceedToSnacks = async () => {
    // Check both localSelectedSeats and global selectedSeats as fallback
    if (!restoreChecked) return;
    setHasProceeded(true);
    const seatsToUse = localSelectedSeats.length > 0 ? localSelectedSeats : (selectedSeats || []);

    // Validate quantity is set
    if (!quantity || quantity < 1 || quantity > 10) {
      showError('Please select the number of tickets (Qty) from the booking page first.');
      return;
    }

    // Validate exact quantity
    if (seatsToUse.length !== quantity) {
      showError(`Please select exactly ${quantity} seat(s). Currently selected: ${seatsToUse.length} seat(s).`);
      return;
    }

    // Validate all seats are from the same area
    if (seatsToUse.length > 0) {
      const firstSeatAreaCode = seatsToUse[0].areaCode;
      const allSameArea = seatsToUse.every(seat => seat.areaCode === firstSeatAreaCode);

      if (!allSameArea) {
        showError('All seats must be selected from the same area.');
        return;
      }
    }

    if (seatsToUse.length === 0) {
      showError('Please select at least one seat');
      return;
    }

    // Get Vista IDs
    const vistaCinemaId = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.id;
    const vistaSessionId = showtime?.id || showtime?.sessionId || showtime?.vistaShowId;

    // Get transaction ID - prioritize ref (immediate) > latestTransactionId (from quantity change) > context > location state
    // This ensures we always use the most recent transaction ID, especially after quantity changes
    const currentTransactionId = bookingTransactionId && typeof bookingTransactionId === 'object' 
      ? bookingTransactionId.transactionId 
      : (bookingTransactionId || null);

    // Use ref first (immediately available synchronously), then latestTransactionId state, then context, then location state
    const transactionId = latestTransactionIdRef.current || latestTransactionId || currentTransactionId || locationTransactionId;

    // console.log('Using transaction ID for add-seats:', {
    //   refTransactionId: latestTransactionIdRef.current,
    //   latestTransactionId,
    //   currentTransactionId,
    //   locationTransactionId,
    //   finalTransactionId: transactionId,
    //   bookingTransactionId,
    //   isLatestTransactionIdSet: !!latestTransactionId,
    //   isRefSet: !!latestTransactionIdRef.current,
    // });

    if (!vistaCinemaId || !vistaSessionId || !transactionId) {
      const missingInfo = [];
      if (!vistaCinemaId) missingInfo.push('cinema ID');
      if (!vistaSessionId) missingInfo.push('session ID');
      if (!transactionId) missingInfo.push('transaction ID');
      showError(`Missing booking information: ${missingInfo.join(', ')}. Please go back and select a showtime again.`);
      setIsConfirmingSeats(false);
      return;
    }

    // Validate transaction ID is not empty string
    if (!String(transactionId).trim()) {
      showError('Transaction ID is invalid. Please go back and select a showtime again.');
      setIsConfirmingSeats(false);
      return;
    }

    // Get ticketTypeCode from first seat (all seats should have same area/ticketType)
    const ticketTypeCode = seatsToUse[0]?.ticketTypeCode;
    if (!ticketTypeCode) {
      showError('Missing ticket type information. Please go back and select seats again.');
      return;
    }

    // Ensure local state is in sync
    if (seatsToUse !== localSelectedSeats) {
      setLocalSelectedSeats(seatsToUse);
    }

    // Show loading state
    setIsConfirmingSeats(true);

    // // Log transaction ID being used for debugging
    // console.log('Attempting to add seats with transaction ID:', {
    //   transactionId,
    //   vistaCinemaId,
    //   vistaSessionId,
    //   ticketTypeCode,
    //   quantity,
    //   latestTransactionId,
    //   currentTransactionId: bookingTransactionId && typeof bookingTransactionId === 'object' 
    //     ? bookingTransactionId.transactionId 
    //     : (bookingTransactionId || null),
    //   locationTransactionId,
    // });

    try {
      // Step 1: Add seats to Vista booking
      const addSeatsResponse = await api.addSeatsToVistaBooking(
        vistaCinemaId,
        vistaSessionId,
        transactionId,
        ticketTypeCode,
        quantity,
        true // userSelectedSeating
      );

      // console.log('Add seats response:', addSeatsResponse);

      // Step 2: Build seats string in format "|<qty>|<seatId1>|<seatId2>|..."
      // Get seat IDs from the seat layout (the original seatId from Vista API)
      // The Vista seatId format is like "0000000006|1|1|2" (from seat.seatId in API response)
      const seatIds = seatsToUse.map(seat => {
        // Priority: seatId (from API) > vistaSeatId (stored during transformation) > id (fallback)
        // The seatId from Vista API is in format "0000000006|1|1|2"
        return seat.seatId || seat.vistaSeatId || seat.id;
      }).filter(Boolean); // Remove any undefined/null values

      if (seatIds.length !== seatsToUse.length) {
        throw new Error('Some seats are missing seat IDs. Please try selecting seats again.');
      }

      // Build seats string: "|<qty>|<seatId1>|<seatId2>|..."
      const seatsString = `|${quantity}|${seatIds.join('|')}|`;

      // Step 3: Set seats in Vista booking
      const setSeatsResponse = await api.setSeatsInVistaBooking(
        vistaCinemaId,
        vistaSessionId,
        transactionId,
        seatsString
      );

      // Check if operation was successful
      const isSuccess = setSeatsResponse?.Success === true || setSeatsResponse?.success === true;

      if (!isSuccess) {
        throw new Error(setSeatsResponse?.StrException || setSeatsResponse?.info || 'Seat confirmation failed');
      }

      // Navigate to snacks page with booking data
      // Pass transaction ID in location state to ensure it's available immediately
      const transactionIdToPass = latestTransactionIdRef.current || latestTransactionId || 
        (bookingTransactionId && typeof bookingTransactionId === 'object' 
          ? bookingTransactionId.transactionId 
          : (bookingTransactionId || null));
      const sessionIdToPass = showtime?.id || showtime?.sessionId || showtime?.vistaShowId;

      sessionStorage.setItem(
        'seat-booking',
        JSON.stringify({
          movie,
          showtime,
          theater,
          selectedSeats: seatsToUse, 
          quantity,
          vistaTransactionId: transactionIdToPass,
          vistaSessionId: sessionIdToPass,
        })
      );
      

      navigate('/snacks', {
        state: {
          movie,
          showtime,
          theater,
          selectedSeats: seatsToUse,
          vistaTransactionId: transactionIdToPass,
          vistaSessionId: sessionIdToPass
        }
      });
    } catch (error) {
      console.error('Error confirming seats:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        transactionId,
        vistaCinemaId,
        vistaSessionId,
      });

      // Provide more detailed error message
      let errorMessage = 'Failed to confirm seats. ';
      if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again.';
      }

      // If transaction ID might be invalid, suggest going back
      if (error.message?.includes('transaction') || error.message?.includes('Transaction')) {
        errorMessage += ' The transaction may have expired. Please go back and select your seats again.';
      }

      showError(errorMessage);
    } finally {
      setIsConfirmingSeats(false);
    }
  };

    // Handle showtime switch — cancels existing transaction, inits new one for new session
    const handleShowtimeSwitch = async (newShowtime) => {
      const newSessionId = newShowtime.id || newShowtime.sessionId || newShowtime.vistaShowId;
    
      if (newSessionId === currentSessionId) return;

    
      setIsSwitchingShowtime(true);
    
      try {
        const vistaCinemaId = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.id;
    
        // ✅ STEP 1: SWITCH SHOWTIME FIRST
        setShowtime(newShowtime);
        setOverrideShowtime(newShowtime);
    
        // ✅ STEP 2: CLEAR OLD STATE
        setSelectedSeats([]);
        setLocalSelectedSeats([]);
        setVistaTransactionId(null);
    
        // ✅ STEP 3: CREATE NEW TRANSACTION
        const bookingData = await api.reinitVistaBooking(
          vistaCinemaId,
          newSessionId,
          null // 🔥 IMPORTANT → DO NOT pass old transaction
        );
    
        if (!bookingData?.transactionId) {
          throw new Error('Failed to initialize booking');
        }
    
        const newTransId = String(bookingData.transactionId).trim();
    
        latestTransactionIdRef.current = newTransId;
        setLatestTransactionId(newTransId);
    
        setVistaTransactionId({
          transactionId: newTransId,
          sessionId: newSessionId,
        });
    
        // Reset UI
        // setQuantity(null);
        quantityModalShownRef.current = true;
        // setShowQuantityModal(true);
    
      } catch (err) {
        console.error(err);
        showError("Failed to switch showtime");
      } finally {
        setIsSwitchingShowtime(false);
        setPendingSwitchShowtime(null);
      }
    };

    const isShowtimeActive = (showtime) => {
      if (!showtime) return true;
      if (showtime.isElapsed === true || showtime.available === false) return false;
    
      const now = new Date();
      const showDateTime = showtime.startDateTime ? new Date(showtime.startDateTime) : null;
      if (!showDateTime || Number.isNaN(showDateTime.getTime())) return true;
    
      const bookingTimeout = bookingSettings?.bookingTimeout || 15;
      const bufferTime = new Date(showDateTime.getTime() + bookingTimeout * 60000);
      return now < bufferTime;
    };
    
    const getBookingStatus = (showtime) => {
      if (!showtime) return 'available';
      if (showtime.isElapsed === true || !isShowtimeActive(showtime)) return 'elapsed';
      if (showtime.available === false || showtime.availableSeats === 0) return 'fully-booked';
    
      if (
        typeof showtime.availableSeats === 'number' &&
        typeof showtime.totalSeats === 'number' &&
        showtime.totalSeats > 0
      ) {
        const soldSeats = showtime.totalSeats - showtime.availableSeats;
        const occupiedPercent = Math.max(0, Math.min(100, Math.round((soldSeats / showtime.totalSeats) * 100)));
        if (showtime.availableSeats === 0) return 'sold-out';
        if (occupiedPercent >= 55) return 'filling-fast';
      }
    
      return 'available';
    };

  const handleQuantityChange = async (newQuantity) => {
    if (!newQuantity || newQuantity < 1 || newQuantity > 10) {
      showError('Please select a valid quantity (1-10)');
      return;
    }

    if (newQuantity === quantity) {
      // No change, just close modal
      setShowQuantityModal(false);
      return;
    }

    // Get Vista IDs
    const vistaCinemaId = theater?.vistaCinemaId || theater?.vistaCinemaID || theater?.id;
    const vistaSessionId = showtime?.id || showtime?.sessionId || showtime?.vistaShowId;

    if (!vistaCinemaId || !vistaSessionId) {
      showError('Missing booking information. Please go back and select a showtime again.');
      setShowQuantityModal(false);
      return;
    }

    setIsUpdatingQuantity(true);

    try {
      // Get existing transaction ID if available - check all sources
      const existingTransIdFromContext = bookingTransactionId && typeof bookingTransactionId === 'object' 
        ? bookingTransactionId.transactionId 
        : (bookingTransactionId || null);
      const existingTransIdFromLocation = locationTransactionId;
      // Use ref first for immediate access, then state
      const existingTransIdFromLocal = latestTransactionIdRef.current || latestTransactionId;
      const existingTransId = existingTransIdFromLocal || existingTransIdFromContext || existingTransIdFromLocation;

      // console.log('Reinitializing booking with quantity change:', {
      //   newQuantity,
      //   existingTransId,
      //   sources: {
      //     local: existingTransIdFromLocal,
      //     context: existingTransIdFromContext,
      //     location: existingTransIdFromLocation,
      //   },
      //   vistaCinemaId,
      //   vistaSessionId,
      // });

      // Cancel existing transaction (if exists) and initialize new Vista booking
      const bookingData = await api.reinitVistaBooking(
        vistaCinemaId, 
        vistaSessionId, 
        existingTransId || null
      );

      // console.log('Reinit booking response:', bookingData);

      if (bookingData && bookingData.transactionId) {
        const newTransactionIdValue = String(bookingData.transactionId).trim();

        if (!newTransactionIdValue) {
          throw new Error('Received empty transaction ID from booking initialization');
        }

        // Update quantity in context
        setQuantity(newQuantity);

        // Update transaction ID in ref FIRST for immediate synchronous access
        latestTransactionIdRef.current = newTransactionIdValue;

        // Update transaction ID in local state
        setLatestTransactionId(newTransactionIdValue);
        // console.log('Set latestTransactionId to:', newTransactionIdValue);

        // Update transaction ID in context - this is critical for subsequent API calls
        const newTransactionId = {
          transactionId: newTransactionIdValue,
          sessionId: vistaSessionId,
        };
        setVistaTransactionId(newTransactionId);

        // Also update location state transaction ID to ensure it's available immediately
        // This ensures the transaction ID is available even before context updates propagate
        if (window.history && window.history.replaceState) {
          const currentState = window.history.state || {};
          window.history.replaceState({
            ...currentState,
            ...location.state,
            vistaTransactionId: newTransactionIdValue,
            vistaSessionId: vistaSessionId,
          }, '');
        }

        // Clear selected seats since quantity changed
        setSelectedSeats([]);
        setLocalSelectedSeats([]);

        // Close modal
        setShowQuantityModal(false);

        // Show success message
        // alert(`Quantity updated to ${newQuantity}. Please select your seats again.`);

        // console.log('Transaction ID updated after quantity change:', {
        //   newTransactionId: newTransactionIdValue,
        //   sessionId: vistaSessionId,
        //   cancelledExisting: bookingData.cancelledExistingTransaction,
        // });
      } else {
        throw new Error('Failed to get transaction ID from booking initialization');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      // alert(`Failed to update quantity: ${error.message || 'Please try again.'}`);
    } finally {
      setIsUpdatingQuantity(false);
    }
  };
  if (!restoreChecked) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-3">Loading booking details…</p>
      </div>
    );
  }
  if (!movie || !showtime || !theater) {
    return null;
  }

  return (
    <>
      {/* Quick Navigation */}
      {/* <div className="quicknav">
        <div className="container">
          <ul>
            <li><a href="#">Group Booking</a></li>
            <li><a href="#">Offers & Promotion</a></li>
            <li><a href="#">FAQ</a></li>
            <li><Link to="/contact-us">Contact Us</Link></li>
          </ul>
        </div>
      </div> */}

      {/* Booking Progress */}
      <div className="booking-progress">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <button
              className="secondary-btn"
              onClick={handleBackClick}
              disabled={isConfirmingSeats || isSwitchingShowtime}
            >
              <i className="fas fa-arrow-left" style={{ fontSize: "1rem" }}></i>
            </button>
            <ProgressStepper
              currentStep="seats"
              movie={movie}
              showtime={showtime}
              theater={theater}
              selectedSeats={selectedSeats}
            />
          </div>
        </div>
      </div>

      {/* Loading Overlay for Seat Confirmation */}
      {isConfirmingSeats && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "10px",
              textAlign: "center",
              minWidth: "300px",
            }}
          >
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ marginBottom: "1rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 style={{ marginBottom: "0.5rem" }}>Confirming your seats...</h5>
            <p style={{ color: "#6c757d", margin: 0 }}>
              Please wait while we confirm your seat selection
            </p>
          </div>
        </div>
      )}

      {/* Seat Selection Component */}
      <SeatSelection
        key={currentSessionId} //showtimechange
        movie={movie}
        showtime={showtime}
        theater={theater}
        onSeatSelect={handleSeatSelect}
        onProceed={handleProceedToSnacks}
        selectedSeats={selectedSeats}
        vistaTransactionId={vistaTransactionId}
        onQuantityClick={() => setShowQuantityModal(true)}
        availableShowtimes={availableShowtimes} //showtimechange
        onShowtimeSwitch={handleShowtimeSwitch}
        isSwitchingShowtime={isSwitchingShowtime}
        pendingSwitchShowtime={pendingSwitchShowtime}
        setPendingSwitchShowtime={setPendingSwitchShowtime}
        showtimeSwitcherOpen={showtimeSwitcherOpen}
        setShowtimeSwitcherOpen={setShowtimeSwitcherOpen}
        getBookingStatus={getBookingStatus}
        isShowtimeActive={isShowtimeActive}
      />

      {/* FAB Button */}
      {/* <div className="fab-btn">
        <a href="#" className="primary-btn help-btn">
          <img src="/assets/img/icons/help.svg" alt="Help" /> Help
        </a>
        <a href="#" className="primary-btn fab-mob pulse">
          <i className="fas fa-ticket"></i>
        </a>
      </div> */}

      {/* Cancel Transaction Confirmation Popup */}
      <CancelTransactionPopup
        isOpen={showCancelConfirm}
        onConfirm={handleCancelTransaction}
        onCancel={() => setShowCancelConfirm(false)}
        isProcessing={isCancellingTransaction}
      />

      {/* Quantity Selection Modal */}
      {showQuantityModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!isUpdatingQuantity && !isCancellingTransaction) {
              navigate("/", { replace: true });
            }
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Select Quantity</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  if (!isUpdatingQuantity) {
                    navigate("/", { replace: true });
                  }
                }}
                disabled={isUpdatingQuantity}
              >
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Select the number of tickets. After that, just click any seat —
                we'll auto-pick the best available seats for you, and you can
                pick from multiple rows within the same category if needed.
              </p>
              <div className="form-group">
                <label htmlFor="modal-quantity">Select Qty</label>
                <select
                  id="modal-quantity"
                  className="form-control"
                  value={quantity || ""}
                  onChange={(e) => {
                    const newQty = parseInt(e.target.value) || 1;
                    // If this is after cancel, use different handler
                    if (isQuantityModalAfterCancel) {
                      handleQuantitySelectAfterCancel(newQty);
                    } else {
                      handleQuantityChange(newQty);
                    }
                  }}
                  disabled={isUpdatingQuantity}
                >
                  <option value="">Select Qty</option>
                  {/* {Array.from({ length: (bookingSettings?.maxSeatsPerBooking || 8) }, (_, i) => i + 1).map((qty) => (
                    <option key={qty} value={qty}>{qty}</option>
                  ))} */}
                  {Array.from(
                    { length: maxSelectableQuantity },
                    (_, i) => i + 1,
                  ).map((qty) => (
                    <option key={qty} value={qty}>
                      {qty}
                    </option>
                  ))}
                </select>
                {currentShowtimeAvailableSeats !== null &&
                  currentShowtimeAvailableSeats <
                    (bookingSettings?.maxSeatsPerBooking || 8) && (
                    <p
                      style={{
                        fontSize: "0.85em",
                        color: "#e67e22",
                        marginTop: "8px",
                        marginBottom: 0,
                      }}
                    >
                      Only {currentShowtimeAvailableSeats} seat(s) available for
                      this show.
                    </p>
                  )}
              </div>
              {isUpdatingQuantity && (
                <div style={{ marginTop: "15px", textAlign: "center" }}>
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{ marginBottom: "0.5rem" }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p style={{ color: "#6c757d", margin: 0, fontSize: "0.9em" }}>
                    Updating booking...
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  if (!isUpdatingQuantity) {
                    navigate("/", { replace: true });
                  }
                }}
                disabled={isUpdatingQuantity}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showErrorPopup && (
        <div
          className="error-popup-overlay"
          onClick={() => setShowErrorPopup(false)}
        >
          <div
            className="error-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="error-popup-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="error-popup-message">{errorMessage}</div>
            <button
              className="error-popup-button"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
      {showSeatWarning && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Limited Seats Available</h5>
            </div>
            <div className="modal-body">
              <p>
                This showtime only has{" "}
                <strong>{currentShowtimeAvailableSeats}</strong> seat(s)
                available, but you selected <strong>{quantity}</strong>{" "}
                ticket(s). Please select a new quantity to continue.
              </p>
            </div>
            <div
              className="modal-footer"
              style={{ display: "flex", gap: "8px" }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowSeatWarning(false);
                  navigate(-1); // go back
                }}
              >
                Go Back
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowSeatWarning(false);
                  setQuantity(null);
                  setSelectedSeats([]);
                  setLocalSelectedSeats([]);
                  setIsQuantityModalAfterCancel(true);
                  quantityModalShownRef.current = false;
                  setShowQuantityModal(true);
                }}
              >
                Select New Quantity
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SeatSelectionPage;